import React from 'react';
import { Box, Container, Typography, Grid, Paper, Chip } from '@mui/material';
import { motion } from 'framer-motion';

const SuccessStories = ({ showTrustBadges = true }) => {
  const testimonials = [
    {
      name: "Brian Huynh",
      role: "Software Engineer @ TCS",
      avatar: "BH",
      quote: "So far, I'm really enjoying the course. After completing hundreds of LeetCode problems, I still felt like something was missing in my understanding of data structures and algorithms. Your course has been a refreshing change — I especially appreciate how clearly you explain the underlying logic and math behind each topic.",
      increase: "Master's in CS @ ASU",
      rating: 5,
      linkedin: "https://www.linkedin.com/in/brian-huynhcs/"
    },
    {
      name: "David K.", 
      role: "Software Engineer at Google",
      avatar: "DK",
      quote: "Been stuck on LeetCode for what felt like forever... Found this course and wow. The pattern templates clicked instantly - they break down problems in a way I've never seen before. Just landed an L4 role at Google ($300K TC). Still can't believe it!",
      increase: "Google Hire",
      rating: 5
    },
    {
      name: "Justin M.",
      role: "Software Engineer at Microsoft",
      avatar: "JM", 
      quote: "These templates are genius. Used to spend hours memorizing solutions, getting nowhere. Now I actually understand the patterns. Nailed my Microsoft onsite - they said my problem-solving approach was exceptional. Can't thank you enough!",
      increase: "Microsoft Hire",
      rating: 5
    }
  ];

  return (
    <Box sx={{ bgcolor: 'rgba(97, 218, 251, 0.02)', py: 8 }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h3" 
          align="center" 
          sx={{ 
            color: 'text.primary', 
            mb: 2,
            fontWeight: 700
          }}
        >
          Success Stories
        </Typography>
        <Typography 
          variant="h6" 
          align="center" 
          sx={{ 
            color: 'text.secondary', 
            mb: 6,
            maxWidth: 600,
            mx: 'auto'
          }}
        >
          Join thousands of engineers who've transformed their careers with TopSWE
        </Typography>

        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    bgcolor: 'rgba(97, 218, 251, 0.05)',
                    border: '1px solid rgba(97, 218, 251, 0.1)',
                    borderRadius: 3,
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(97, 218, 251, 0.2)',
                      bgcolor: 'rgba(97, 218, 251, 0.08)'
                    }
                  }}
                >
                  {/* Quote mark */}
                  <Typography 
                    sx={{ 
                      position: 'absolute',
                      top: 15,
                      left: 20,
                      fontSize: '3rem',
                      color: 'rgba(97, 218, 251, 0.3)',
                      fontFamily: 'serif'
                    }}
                  >
                    "
                  </Typography>

                  {/* Stars */}
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 2, justifyContent: 'center' }}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Typography key={i} sx={{ color: '#ffd700', fontSize: '1.2rem' }}>
                        ⭐
                      </Typography>
                    ))}
                  </Box>

                  <Typography 
                    sx={{ 
                      color: 'text.primary', 
                      mb: 3, 
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                      fontSize: '1.1rem'
                    }}
                  >
                    {testimonial.quote}
                  </Typography>

                  {/* Achievement badge */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Chip 
                      label={testimonial.increase}
                      sx={{
                        bgcolor: 'action.hover',
                        color: testimonial.increase.includes('$') ? 'success.main' : 'secondary.main',
                        border: '1px solid',
                        borderColor: testimonial.increase.includes('$') ? 'success.main' : 'divider',
                        fontWeight: 700,
                        fontSize: '0.9rem'
                      }}
                    />
                  </Box>

                  {/* Profile */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'secondary.main',
                        fontWeight: 700,
                        fontSize: '1.2rem'
                      }}
                    >
                      {testimonial.avatar}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {testimonial.name}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                        {testimonial.role}
                      </Typography>
                      {testimonial.linkedin && (
                        <Typography 
                          component="a"
                          href={testimonial.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            color: 'secondary.main', 
                            fontSize: '0.8rem',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          LinkedIn →
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Trust badges */}
        {showTrustBadges && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
              {[
                { label: '40,000+', sublabel: 'Students Influenced'},
                { label: '100%', sublabel: 'Interview Success Rate' },
                { label: '100%', sublabel: 'Satisfaction Guarantee'},
                { label: '5/5', sublabel: 'Student Rating' }
              ].map((stat, index) => (
                <Box key={index} sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      color: 'secondary.main', 
                      fontWeight: 700,
                      mb: 0.5
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>
                    {stat.sublabel}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SuccessStories;
